import { useMutation } from "@tanstack/react-query";
import { api, type PartialError } from "@/lib/api";
import type { FilterGroup } from "@/lib/filters";
import {
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";
import type { ChannelMetrics } from "./use-channel-metrics";

/**
 * Channel type for consistency
 */
export interface Channel {
  name: string;
  id?: string;
}

/**
 * Channel with embedded metrics
 */
export interface ChannelWithMetrics extends Channel {
  metrics?: ChannelMetrics;
}

/**
 * Channel with flattened metrics for CSV export
 */
export interface ChannelWithFlattenedMetrics extends Channel {
  [key: string]: string | number | undefined;
}

/**
 * Response type for channels list API
 */
export interface ChannelsResponse {
  results: Channel[];
  total: number;
}

/**
 * Response type for channels with metrics
 */
export interface ChannelsWithMetricsResponse {
  results: ChannelWithMetrics[];
  total: number;
  // Non-fatal errors that occurred during request processing (e.g., metrics timeout).
  errors?: PartialError[] | null;
}

/**
 * Parameters for channels with metrics query
 */
export interface ChannelsQueryParams extends QueryParams {
  metrics?: Array<keyof ChannelMetrics>;
  metric_filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
    campaign_filters?: FilterGroup;
  };
}

/**
 * Hook to fetch all available channels (basic version)
 */
export function useChannels() {
  return api.useQuery<ChannelsResponse>("datahub/channels/");
}

/**
 * Hook to fetch channels with embedded metrics (now supported by enhanced backend)
 */
export function useChannelsWithMetrics(params: ChannelsQueryParams = {}) {
  // Use the same parameter processing as campaigns for consistency
  const searchParams = processQueryParams(params);

  const queryString = searchParams.toString();
  const endpoint = queryString
    ? `datahub/channels/?${queryString}`
    : "datahub/channels/";

  // Now the backend supports embedded metrics just like campaigns
  return api.useQuery<ChannelsWithMetricsResponse>(endpoint, {
    enabled: true,
  });
}

/**
 * Hook to fetch specific channels by IDs
 */
export function useChannelsByIds(channelIds?: string[]) {
  const channelIdsParam = channelIds?.join(",");
  return api.useQuery<ChannelsResponse>(
    `datahub/channels/by-ids/?channel_ids=${channelIdsParam}`,
    {
      enabled: !!channelIds && channelIds.length > 0,
    },
  );
}

/**
 * Hook to search channels by name or ID
 */
export function useChannelSearch(search?: string) {
  return api.useQuery<ChannelsResponse>(
    `datahub/channels/search/?search=${encodeURIComponent(search || "")}`,
    {
      enabled: !!search && search.length > 1, // Only search if at least 2 characters
    },
  );
}

/**
 * Fetch all channels matching the given filters for CSV export
 * Since channels are a small set, we can get them all in one call
 */
export function useChannelsExport() {
  const mutation = useMutation<
    ChannelWithFlattenedMetrics[],
    Error,
    ChannelsQueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      // Process parameters into a URLSearchParams object
      const searchParams = processQueryParams({
        ...params,
        limit: 500, // Set a high limit to get all channels
      });

      // Fetch all channels with metrics in a single call
      const response = await api.get<ChannelsWithMetricsResponse>(
        `datahub/channels/?${searchParams}`,
      );

      // Transform to flatten metrics for CSV export
      return response.results.map((channel) => {
        const { metrics, ...baseObject } = channel;

        // Create a new object with all base properties
        const channelWithMetrics: ChannelWithFlattenedMetrics = {
          ...baseObject,
        };

        // Add each metric as a top-level property if metrics exist
        if (metrics) {
          Object.entries(metrics).forEach(([key, value]) => {
            // Only add primitive values to avoid nested objects in CSV
            if (
              typeof value === "string" ||
              typeof value === "number" ||
              typeof value === "boolean" ||
              value === null ||
              value === undefined
            ) {
              channelWithMetrics[`metric_${key}`] = value;
            }
          });
        }

        return channelWithMetrics;
      });
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportChannelsAsync: (params?: ChannelsQueryParams) =>
      mutation.mutateAsync(params),
  };
}
